import * as React from "react";

import { SMS_AUTH_CODE_LENGTH } from "@/constants/smsAuthCode";
import { isAndroid, isNativeApp } from "@/utilities/capacitorLogic";
import { SmsUserConsent } from "@/utilities/smsUserConsentPlugin";

type Options = {
  /** 入力待ちの間だけ true。送信中・完了後は false にして待受を止める */
  enabled: boolean;
  /** 取り出した桁数ぶんのコード。呼び出し側で入力欄へ反映して送信する */
  onCode: (code: string) => void;
  /** 値が変わるたびに待受をやり直す。再送で新しい SMS を待つときにカウンタを進める */
  restartKey?: number;
};

// ブラウザが WebOTP API に対応しているか（Android Chrome）。iOS Safari は OS の AutoFill が候補を出すため未対応で問題ない
const supportsWebOtp = (): boolean =>
  "OTPCredential" in window && typeof window.navigator.credentials?.get === "function";

const digitsOnly = (value: string | undefined | null): string => (value ?? "").replace(/\D/g, "");

/**
 * 端末に届いた SMS から認証コードを受け取り、onCode に渡す（Trello #157）。
 * - Android アプリ: SMS User Consent API（自作プラグイン）。OS の許可ダイアログで承認されたコードを受け取る
 * - ブラウザ（Android Chrome）: WebOTP API。SMS 末尾の「@ドメイン #コード」行と画面のドメインが一致したとき候補が出る
 * - iOS（Safari・アプリ）: OS の AutoFill が入力欄の autocomplete="one-time-code" に候補を出すので、ここでは何もしない
 * いずれも取得できなかったときは手入力に任せ、エラーは出さない。
 */
export const useSmsCodeAutofill = ({ enabled, onCode, restartKey = 0 }: Options): void => {
  // 最新の onCode を効果の再実行なしで呼ぶ（呼び出し側が毎レンダー関数を作り直しても待受を張り直さない）
  const onCodeRef = React.useRef(onCode);
  onCodeRef.current = onCode;

  React.useEffect(() => {
    if (!enabled) return undefined;

    if (isNativeApp) {
      if (!isAndroid) return undefined;
      let active = true;
      SmsUserConsent.start({ codeLength: SMS_AUTH_CODE_LENGTH })
        .then(({ code }) => {
          const digits = digitsOnly(code);
          if (active && digits.length === SMS_AUTH_CODE_LENGTH) onCodeRef.current(digits);
        })
        .catch(() => {
          // 張り直しの上限超え・タイムアウト・stop() による中断・Play Services なし。手入力に任せる
        });
      return () => {
        active = false;
        SmsUserConsent.stop().catch(() => {
          // 停止の失敗は無視してよい（次の start() が前の待受を片付ける）
        });
      };
    }

    if (!supportsWebOtp()) return undefined;
    const controller = new AbortController();
    window.navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: controller.signal })
      .then((credential) => {
        const digits = digitsOnly((credential as OTPCredential | null)?.code);
        if (!controller.signal.aborted && digits.length === SMS_AUTH_CODE_LENGTH) onCodeRef.current(digits);
      })
      .catch(() => {
        // abort・ユーザーの拒否・未対応。手入力に任せる
      });
    return () => controller.abort();
  }, [enabled, restartKey]);
};
